'use client';

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import {
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  Stethoscope,
  HeartPulse,
  Activity,
  Scissors,
  Pill,
  Syringe,
  Baby,
  Calendar,
  Clock,
  Shield,
  MapPin,
  Phone,
  FileText,
  CheckCircle,
  UserRound,
} from 'lucide-react';
import ScrollReveal from '@/components/shared/ScrollReveal';
import type { Banner, Treatment, GuideItem } from '@/types';
import type { EquipmentItem } from '@/data';
import type { PublicHospitalInfo } from '@/data/hospital';
import type {
  HospitalNewsEntity,
  MedicalArticleEntity,
} from '@/data/content';
import type { DepartmentEntity } from '@/data/departments';
import type { PhysicianEntity } from '@/data/physicians';

const iconMap: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  Stethoscope, HeartPulse, Activity, Scissors, Pill, Syringe, Baby,
  Calendar, Clock, Shield, MapPin, Phone, FileText,
};

export interface HomeViewProps {
  banners: Banner[];
  treatments: Treatment[];
  guideItems: GuideItem[];
  equipment: EquipmentItem[];
  hospitalInfo: PublicHospitalInfo;
  departments: DepartmentEntity[];
  physicians: PhysicianEntity[];
  newsItems: HospitalNewsEntity[];
  articles: MedicalArticleEntity[];
}

export default function HomeView({
  banners,
  treatments,
  guideItems,
  equipment,
  hospitalInfo,
  departments,
  physicians,
  newsItems,
  articles,
}: HomeViewProps) {
  const [currentBanner, setCurrentBanner] = useState(0);

  const bannerCount = banners.length;
  const shownPhysicians = physicians.slice(0, 4);

  const nextBanner = useCallback(() => {
    setCurrentBanner((prev) => (prev + 1) % bannerCount);
  }, [bannerCount]);

  const prevBanner = () => {
    setCurrentBanner((prev) => (prev - 1 + bannerCount) % bannerCount);
  };

  useEffect(() => {
    if (bannerCount <= 1) return;
    const timer = setInterval(nextBanner, 5000);
    return () => clearInterval(timer);
  }, [nextBanner, bannerCount]);

  return (
    <div>
      {/* Hero Banner */}
      <section className="relative h-[50vh] md:h-[70vh] overflow-hidden">
        {banners.map((banner, index) => (
          <div
            key={banner.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === currentBanner ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-[#1E88E5]/80 to-[#26A69A]/60 z-10" />
            <img
              src={banner.image}
              alt={banner.title}
              width={1920}
              height={1080}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 z-20 flex items-center">
              <div className="max-w-7xl mx-auto px-4 w-full">
                <div className="max-w-2xl">
                  {index === 0 ? (
                    <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
                      {banner.title}
                    </h1>
                  ) : (
                    <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
                      {banner.title}
                    </h2>
                  )}
                  <p className="text-lg md:text-xl text-white/90 mb-8">
                    {banner.subtitle}
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <Link
                      href="/guide"
                      className="px-6 py-3 bg-white text-[#1E88E5] rounded-lg font-medium hover:bg-gray-100 transition-colors"
                    >
                      预约挂号
                    </Link>
                    <Link
                      href="/departments"
                      className="px-6 py-3 border-2 border-white text-white rounded-lg font-medium hover:bg-white/10 transition-colors"
                    >
                      了解科室
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
        {/* Banner controls：移动端隐藏箭头，避免压住文案（圆点仍可切换） */}
        <button onClick={prevBanner} className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-30 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full items-center justify-center text-white transition-colors">
          <ChevronLeft size={24} />
        </button>
        <button onClick={nextBanner} className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-30 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full items-center justify-center text-white transition-colors">
          <ChevronRight size={24} />
        </button>
        {/* Dots */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex gap-2">
          {banners.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentBanner(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentBanner ? 'w-8 bg-white' : 'w-2 bg-white/50'
              }`}
            />
          ))}
        </div>
      </section>

      {/* Quick Access：整体放在 Banner 下方的白色区域，不与轮播图重叠 */}
      <section className="relative z-10 max-w-6xl mx-auto px-4 py-8 md:py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: Calendar, label: '预约挂号', href: '/guide', color: 'bg-[#1E88E5]' },
            { icon: Clock, label: '门诊时间', href: '/guide', color: 'bg-[#26A69A]' },
            { icon: MapPin, label: '来院路线', href: '/guide', color: 'bg-[#1E88E5]' },
            { icon: Phone, label: '电话咨询', href: `tel:${hospitalInfo.phone}`, color: 'bg-[#26A69A]' },
          ].map((item) => (
            <Link
              key={item.label}
              href={item.href}
              className={`${item.color} text-white rounded-xl p-4 md:p-5 flex items-center gap-3 hover:opacity-90 transition-opacity shadow-lg`}
            >
              <item.icon size={28} />
              <span className="font-medium text-sm md:text-base">{item.label}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Hospital Introduction */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4">
          <ScrollReveal>
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">医院简介</h2>
              <div className="w-16 h-1 bg-gradient-to-r from-[#1E88E5] to-[#26A69A] mx-auto rounded-full" />
            </div>
          </ScrollReveal>
          <ScrollReveal delay={100}>
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <p className="text-gray-600 leading-relaxed mb-6">
                  {hospitalInfo.description}
                </p>
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {hospitalInfo.features.map((feature) => (
                    <div key={feature} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle size={16} className="text-[#26A69A] shrink-0" />
                      {feature}
                    </div>
                  ))}
                </div>
                <Link
                  href="/about"
                  className="inline-flex items-center gap-1 text-[#1E88E5] font-medium hover:gap-2 transition-all"
                >
                  了解更多 <ArrowRight size={16} />
                </Link>
              </div>
              <div className="relative">
                <div className="rounded-xl overflow-hidden shadow-xl">
                  <img
                    src="/images/hospital/clinic-lobby.webp"
                    alt="医院环境"
                    width={1600}
                    height={900}
                    className="w-full h-64 md:h-80 object-cover"
                  />
                </div>
                <div className="absolute -bottom-4 -left-4 bg-[#1E88E5] text-white p-4 rounded-xl shadow-lg">
                  <div className="text-3xl font-bold">1974</div>
                  <div className="text-sm text-white/80">医院资料记载，历史始于</div>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Departments */}
      {departments.length > 0 && (
      <section className="py-16 md:py-20 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-4">
          <ScrollReveal>
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">重点科室</h2>
              <div className="w-16 h-1 bg-gradient-to-r from-[#1E88E5] to-[#26A69A] mx-auto rounded-full" />
              <p className="text-gray-500 mt-4">专业细分，精准诊疗</p>
            </div>
          </ScrollReveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {departments.map((dept, index) => {
              const IconComp = iconMap[dept.icon || ''] || Stethoscope;
              return (
                <ScrollReveal key={dept.id} delay={index * 80}>
                  <Link
                    href={`/departments/${dept.id}`}
                    className="bg-white rounded-xl p-5 text-center hover:shadow-lg transition-all hover:-translate-y-1 group"
                  >
                    <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gradient-to-br from-[#1E88E5]/10 to-[#26A69A]/10 flex items-center justify-center group-hover:from-[#1E88E5] group-hover:to-[#26A69A] transition-all">
                      <IconComp size={24} className="text-[#1E88E5] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-semibold text-gray-800 text-sm">{dept.name}</h3>
                  </Link>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* Expert Team */}
      {physicians.length > 0 && (
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4">
          <ScrollReveal>
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">专家团队</h2>
              <div className="w-16 h-1 bg-gradient-to-r from-[#1E88E5] to-[#26A69A] mx-auto rounded-full" />
              <p className="text-gray-500 mt-4">汇聚肛肠领域资深专家</p>
            </div>
          </ScrollReveal>
          {/* 医生数量不足 4 位时收窄居中，避免网格留半边空白 */}
          <div
            className={`grid grid-cols-2 gap-4 md:gap-6 ${
              shownPhysicians.length > 2
                ? 'lg:grid-cols-4'
                : 'md:grid-cols-2 lg:max-w-4xl lg:mx-auto'
            }`}
          >
            {shownPhysicians.map((doctor, index) => (
              <ScrollReveal key={doctor.id} delay={index * 100} className="h-full">
                <Link
                  href={`/doctors/${doctor.slug}`}
                  className="group flex flex-col h-full bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all overflow-hidden"
                >
                  {/* 头像区：浅色渐变底 + 白边圆形头像 */}
                  <div className="bg-gradient-to-b from-[#1E88E5]/10 to-[#26A69A]/15 px-4 pt-8 pb-6 flex justify-center">
                    <div className="w-24 h-24 lg:w-28 lg:h-28 rounded-full bg-gradient-to-br from-[#1E88E5] to-[#26A69A] flex items-center justify-center text-white overflow-hidden ring-4 ring-white shadow-md group-hover:scale-105 transition-transform">
                      {doctor.image ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={doctor.image}
                          alt={doctor.name}
                          width={128}
                          height={128}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <UserRound size={48} />
                      )}
                    </div>
                  </div>
                  {/* 信息区：姓名 + 职称徽章 + 擅长领域 */}
                  <div className="flex-1 px-4 pb-6 pt-1 text-center">
                    <h3 className="text-base md:text-lg font-bold text-gray-800 group-hover:text-[#1E88E5] transition-colors">
                      {doctor.name}
                    </h3>
                    <div className="mt-2 flex flex-wrap justify-center gap-1.5">
                      {doctor.title.split('、').map((title) => (
                        <span
                          key={title}
                          className="text-xs text-[#1E88E5] bg-[#1E88E5]/10 border border-[#1E88E5]/15 px-2 py-0.5 rounded-full"
                        >
                          {title}
                        </span>
                      ))}
                    </div>
                    <p className="mt-3 text-xs text-gray-500 leading-relaxed line-clamp-2">
                      <span className="font-medium text-[#26A69A]">擅长：</span>
                      {doctor.specialties.join('、')}
                    </p>
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              href="/doctors"
              className="inline-flex items-center gap-1 px-6 py-2.5 border-2 border-[#1E88E5] text-[#1E88E5] rounded-lg font-medium hover:bg-[#1E88E5] hover:text-white transition-colors"
            >
              查看全部专家 <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* Featured Equipment */}
      {treatments.length > 0 && equipment.length > 0 && (
      <>
      <section className="py-16 md:py-20 bg-gradient-to-br from-[#1E88E5] to-[#26A69A]">
        <div className="max-w-7xl mx-auto px-4">
          <ScrollReveal>
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">诊疗设备</h2>
              <div className="w-16 h-1 bg-white/50 mx-auto rounded-full" />
              <p className="text-white/80 mt-4">医院配备的检查与治疗设备，以医院当期情况为准</p>
            </div>
          </ScrollReveal>
          <div className="grid md:grid-cols-3 gap-6">
            {equipment.map((tech, index) => (
              <ScrollReveal key={tech.id} delay={index * 100}>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-white hover:bg-white/20 transition-colors border border-white/20">
                  <div className="text-4xl mb-4">{tech.icon || '🏥'}</div>
                  <h3 className="text-xl font-bold mb-3">{tech.name}</h3>
                  <p className="text-white/80 text-sm leading-relaxed whitespace-pre-line">{tech.description}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Treatments */}
      <section className="py-16 md:py-20 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-4">
          <ScrollReveal>
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">诊疗项目</h2>
              <div className="w-16 h-1 bg-gradient-to-r from-[#1E88E5] to-[#26A69A] mx-auto rounded-full" />
            </div>
          </ScrollReveal>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {treatments.map((t, index) => (
              <ScrollReveal key={t.id} delay={index * 60}>
                <Link
                  href={`/treatments#${t.name}`}
                  className="bg-white rounded-xl p-4 text-center hover:shadow-md transition-all hover:-translate-y-1 group"
                >
                  <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-[#1E88E5]/10 flex items-center justify-center group-hover:bg-[#1E88E5] transition-colors">
                    <HeartPulse size={20} className="text-[#1E88E5] group-hover:text-white transition-colors" />
                  </div>
                  <h4 className="font-medium text-gray-800 text-sm">{t.name}</h4>
                </Link>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      </>
      )}

      {/* News & Health */}
      {(newsItems.length > 0 || articles.length > 0) && (
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* News */}
            <div>
              <ScrollReveal>
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800">新闻动态</h2>
                    <div className="w-12 h-1 bg-[#1E88E5] rounded-full mt-2" />
                  </div>
                  <Link href="/news" className="text-[#1E88E5] text-sm flex items-center gap-1 hover:gap-2 transition-all">
                    更多 <ArrowRight size={14} />
                  </Link>
                </div>
              </ScrollReveal>
              <div className="space-y-4">
                {newsItems.slice(0, 4).map((news, index) => (
                  <ScrollReveal key={news.id} delay={index * 80}>
                    <Link href={`/news/${news.slug}`} className="flex gap-4 group">
                      {news.image && <img src={news.image} alt={news.title} width={240} height={200} className="w-24 h-20 rounded-lg object-cover shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-800 group-hover:text-[#1E88E5] transition-colors line-clamp-2 text-sm">
                          {news.title}
                        </h3>
                        <p className="text-xs text-gray-400 mt-2">{news.datePublished}</p>
                      </div>
                    </Link>
                  </ScrollReveal>
                ))}
              </div>
            </div>

            {/* Health */}
            <div>
              <ScrollReveal>
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800">健康科普</h2>
                    <div className="w-12 h-1 bg-[#26A69A] rounded-full mt-2" />
                  </div>
                  <Link href="/health" className="text-[#26A69A] text-sm flex items-center gap-1 hover:gap-2 transition-all">
                    更多 <ArrowRight size={14} />
                  </Link>
                </div>
              </ScrollReveal>
              <div className="space-y-4">
                {articles.slice(0, 4).map((article, index) => (
                  <ScrollReveal key={article.id} delay={index * 80}>
                    <Link href={`/health/${article.slug}`} className="flex gap-4 group">
                      {article.image && <img src={article.image} alt={article.title} width={240} height={200} className="w-24 h-20 rounded-lg object-cover shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-800 group-hover:text-[#26A69A] transition-colors line-clamp-2 text-sm">
                          {article.title}
                        </h3>
                        <p className="text-xs text-gray-400 mt-2">{article.datePublished}</p>
                      </div>
                    </Link>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Guide Quick Access */}
      <section className="py-16 md:py-20 bg-[#F5F7FA]">
        <div className="max-w-7xl mx-auto px-4">
          <ScrollReveal>
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">就医指南</h2>
              <div className="w-16 h-1 bg-gradient-to-r from-[#1E88E5] to-[#26A69A] mx-auto rounded-full" />
            </div>
          </ScrollReveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {guideItems.map((item, index) => {
              const IconComp = iconMap[item.icon] || FileText;
              return (
                <ScrollReveal key={item.id} delay={index * 80}>
                  <Link
                    href={`/guide#${item.title}`}
                    className="bg-white rounded-xl p-5 text-center hover:shadow-lg transition-all hover:-translate-y-1 group"
                  >
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#1E88E5]/10 flex items-center justify-center group-hover:bg-[#1E88E5] transition-colors">
                      <IconComp size={22} className="text-[#1E88E5] group-hover:text-white transition-colors" />
                    </div>
                    <h4 className="font-medium text-gray-800 text-sm">{item.title}</h4>
                    <p className="text-xs text-gray-500 mt-1">{item.description}</p>
                  </Link>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

    </div>
  );
}
